import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'zhelta_region';

// Region → (language code | currency code)
// Each region provides sensible defaults. Users can still freely override
// language or currency from their individual selectors.
export const REGIONS = [
  { code: 'US', flag: '🇺🇸', name: 'United States',    lang: 'en',    currency: 'USD' },
  { code: 'GB', flag: '🇬🇧', name: 'United Kingdom',   lang: 'en',    currency: 'GBP' },
  { code: 'CA', flag: '🇨🇦', name: 'Canada',           lang: 'fr-CA', currency: 'CAD' },
  { code: 'EU', flag: '🇪🇺', name: 'European Union',   lang: 'en',    currency: 'EUR' },
  { code: 'DE', flag: '🇩🇪', name: 'Germany',          lang: 'de',    currency: 'EUR' },
  { code: 'FR', flag: '🇫🇷', name: 'France',           lang: 'fr',    currency: 'EUR' },
  { code: 'NL', flag: '🇳🇱', name: 'Netherlands',      lang: 'nl',    currency: 'EUR' },
  { code: 'ES', flag: '🇪🇸', name: 'Spain',            lang: 'es',    currency: 'EUR' },
  { code: 'BR', flag: '🇧🇷', name: 'Brazil',           lang: 'pt-BR', currency: 'BRL' },
  { code: 'JP', flag: '🇯🇵', name: 'Japan',            lang: 'ja',    currency: 'JPY' },
  { code: 'CN', flag: '🇨🇳', name: 'China',            lang: 'zh-CN', currency: 'CNY' },
  { code: 'IN', flag: '🇮🇳', name: 'India',            lang: 'en',    currency: 'INR' },
  { code: 'AE', flag: '🇦🇪', name: 'United Arab Emirates', lang: 'ar', currency: 'AED' },
  { code: 'SA', flag: '🇸🇦', name: 'Saudi Arabia',     lang: 'ar',    currency: 'AED' },
  { code: 'IL', flag: '🇮🇱', name: 'Israel',           lang: 'he',    currency: 'USD' },
  { code: 'TH', flag: '🇹🇭', name: 'Thailand',         lang: 'th',    currency: 'USD' },
  { code: 'VN', flag: '🇻🇳', name: 'Vietnam',          lang: 'vi',    currency: 'USD' },
  { code: 'ID', flag: '🇮🇩', name: 'Indonesia',        lang: 'id',    currency: 'USD' },
  { code: 'AU', flag: '🇦🇺', name: 'Australia',        lang: 'en',    currency: 'AUD' },
  { code: 'GH', flag: '🇬🇭', name: 'Ghana',            lang: 'en',    currency: 'GHS' },
];

const RegionContext = createContext({
  region: null,
  setRegion: () => {},
  detected: null,
  detecting: false,
});

export function RegionProvider({ children, onRegionSelected }) {
  const [region, setRegionState] = useState(null);
  const [detected, setDetected] = useState(null);
  const [detecting, setDetecting] = useState(false);

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored && REGIONS.some((r) => r.code === stored)) {
      setRegionState(stored);
      return;
    }
    // First visit: geo-IP auto-detect (non-blocking suggestion only).
    // api.country.is is HTTPS + CORS-friendly with no key; it returns just
    // {ip, country} which is all we need to nudge the region selector.
    setDetecting(true);
    fetch('https://api.country.is/')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.country) return;
        const match = REGIONS.find((r) => r.code === data.country);
        if (match) setDetected(match.code);
      })
      .catch(() => {
        /* ignore — geo detection is a nicety */
      })
      .finally(() => setDetecting(false));
  }, []);

  const setRegion = useCallback(
    (code) => {
      const entry = REGIONS.find((r) => r.code === code);
      if (!entry) return;
      setRegionState(code);
      try {
        window.localStorage.setItem(STORAGE_KEY, code);
      } catch (_e) {
        /* ignore */
      }
      onRegionSelected?.(entry);
    },
    [onRegionSelected]
  );

  return (
    <RegionContext.Provider value={{ region, setRegion, detected, detecting }}>
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion() {
  return useContext(RegionContext);
}
